import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './SecretSantaLink.css'
import { generateSecretSantaPairs } from './utils/secretSanta'
import { encryptText } from './utils/burnCrypto'
import { createBurnMessage } from './utils/burnMessageStore'
import { parseSpreadsheetNames } from './utils/spreadsheetImport'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const canShare = typeof navigator !== 'undefined' && !!navigator.share

const mergeUniqueNames = (current, imported) => {
  const merged = [...current]
  imported.forEach((name) => {
    if (!merged.includes(name)) merged.push(name)
  })
  return merged
}

const SecretSantaLink = () => {
  useDocumentMeta({
    title: 'Amigo Secreto Online com Link | jarder.dev',
    description:
      'Sorteie o amigo secreto e gere um link individual para cada ' +
      'participante, que só pode ser aberto uma única vez, sem grupo ' +
      'de WhatsApp revelando quem tirou quem.',
  })

  const [participants, setParticipants] = useState([])
  const [nameInput, setNameInput] = useState('')
  const [links, setLinks] = useState([])
  const [sentMap, setSentMap] = useState({})
  const [error, setError] = useState('')
  const [status, setStatus] = useState('idle')
  const fileInputRef = useRef(null)

  const addParticipant = () => {
    const trimmed = nameInput.trim()
    if (!trimmed) return
    if (participants.includes(trimmed)) {
      setError('Esse nome já foi adicionado.')
      return
    }
    setParticipants((current) => [...current, trimmed])
    setNameInput('')
    setError('')
  }

  const removeParticipant = (name) => {
    setParticipants((current) => current.filter((p) => p !== name))
  }

  const handleNameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addParticipant()
    }
  }

  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const imported = parseSpreadsheetNames(String(reader.result))
      setParticipants((current) => mergeUniqueNames(current, imported))
      trackEvent('tool_import', {
        tool: 'secret_santa',
        names_count: imported.length,
      })
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  const draw = async () => {
    if (participants.length < 3) {
      setError('Adicione pelo menos 3 participantes.')
      setLinks([])
      return
    }
    const pairs = generateSecretSantaPairs(participants)
    if (!pairs) {
      setError('Não foi possível sortear. Tente novamente.')
      return
    }
    setError('')
    setStatus('loading')
    try {
      const base = `${window.location.origin}/ferramentas/amigo-secreto/revelar`
      const generatedLinks = await Promise.all(
        pairs.map(async (pair) => {
          const { ciphertext, iv, keyBase64Url } = await encryptText(
            JSON.stringify(pair),
          )
          const id = await createBurnMessage({ ciphertext, iv })
          return { from: pair.from, url: `${base}?id=${id}#${keyBase64Url}` }
        }),
      )
      setLinks(generatedLinks)
      setSentMap({})
      setStatus('ready')
      trackEvent('tool_generate', {
        tool: 'secret_santa',
        count: participants.length,
      })
    } catch {
      setError('Não foi possível gerar os links agora. Tente novamente.')
      setStatus('idle')
    }
  }

  const markSent = (name) =>
    setSentMap((current) => ({ ...current, [name]: true }))

  const copyLink = (link) => {
    navigator.clipboard.writeText(link.url)
    markSent(link.from)
    trackEvent('tool_copy', { tool: 'secret_santa' })
  }

  const shareLink = async (link) => {
    try {
      await navigator.share({
        title: 'Amigo Secreto',
        text: `${link.from}, aqui está seu link do amigo secreto:`,
        url: link.url,
      })
      markSent(link.from)
      trackEvent('tool_generate', { tool: 'secret_santa_share' })
    } catch {
      // User cancelled the share sheet — not an error worth surfacing.
    }
  }

  const whatsappLink = (link) =>
    `https://wa.me/?text=${encodeURIComponent(
      `${link.from}, aqui está seu link do amigo secreto: ${link.url}`,
    )}`

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Amigo Secreto com Link</h1>
      <p className="tool-disclaimer">
        Sorteia e gera um link individual para cada pessoa, mande cada
        link só para o respectivo participante. Cada link{' '}
        <strong>só pode ser aberto uma única vez</strong>: assim que a
        pessoa revelar quem ela tirou, o link se torna inválido pra
        sempre. Nem ela, nem você (organizador), nem mais ninguém
        consegue abri-lo de novo depois disso.
      </p>

      <div className="tool-field">
        <label htmlFor="participant-name">
          Participantes ({participants.length})
        </label>
        <div className="chip-input-row">
          <input
            id="participant-name"
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={handleNameKeyDown}
            placeholder="Digite um nome e aperte Enter"
          />
          <button
            type="button"
            className="tool-button tool-button-secondary"
            onClick={addParticipant}
          >
            Adicionar
          </button>
        </div>
      </div>

      {participants.length > 0 && (
        <ul className="chip-list">
          {participants.map((name) => (
            <li className="chip" key={name}>
              {name}
              <button
                type="button"
                className="chip-remove"
                onClick={() => removeParticipant(name)}
                aria-label={`Remover ${name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tool-import-row">
        <button
          type="button"
          className="tool-button tool-button-secondary"
          onClick={() => fileInputRef.current.click()}
        >
          <span role="img" aria-label="arquivo">
            📄
          </span>{' '}
          Importar planilha (.csv/.txt)
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          onChange={handleFileImport}
          hidden
        />
        <span className="tool-import-hint">
          Nomes importados são adicionados à lista acima.
        </span>
      </div>

      <button
        type="button"
        className="tool-button"
        onClick={draw}
        disabled={status === 'loading'}
      >
        {status === 'loading' ? 'Gerando links...' : 'Sortear e gerar links'}
      </button>

      {error && <p className="tool-error">{error}</p>}

      {links.length > 0 && (
        <ul className="secret-santa-links">
          {links.map((link) => (
            <li
              key={link.from}
              className={sentMap[link.from] ? 'secret-santa-sent' : ''}
            >
              <span>
                {link.from}
                {sentMap[link.from] && (
                  <span className="secret-santa-sent-tag">enviado</span>
                )}
              </span>
              <div className="secret-santa-link-actions">
                <button
                  type="button"
                  className="tool-button tool-button-secondary"
                  onClick={() => copyLink(link)}
                >
                  Copiar
                </button>
                <a
                  className="tool-button tool-button-secondary"
                  href={whatsappLink(link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => markSent(link.from)}
                >
                  WhatsApp
                </a>
                {canShare && (
                  <button
                    type="button"
                    className="tool-button tool-button-secondary"
                    onClick={() => shareLink(link)}
                  >
                    Compartilhar
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="tool-info-section">
        <h2>Por que usar links em vez de grupo</h2>
        <p>
          Grupos de WhatsApp para amigo secreto sempre correm o risco de
          alguém ver a mensagem errada ou dar spoiler sem querer. Aqui,
          cada link é criptografado individualmente (a mesma tecnologia
          usada no{' '}
          <Link to="/ferramentas/texto-temporario">texto temporário</Link>
          ) e só pode ser aberto uma vez, por quem recebeu aquele link
          específico  nem o organizador consegue ver quem tirou quem
          depois de gerado.
        </p>
      </div>
    </div>
  )
}

export default SecretSantaLink
